import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Tabs, Button, Space, Input, Select, Switch, Typography, Radio, InputNumber } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { TabPane } = Tabs;
const { Text } = Typography;

/** Helper to generate unique IDs for new sorts/comparisons, etc. */
const generateId = () => Math.random().toString(36).substr(2, 9);

/* ------------------------------------------------------------------
   Basic green pill style (for wheels/bars, sorts, comparisons).
   Removed old border so no unwanted outline.
------------------------------------------------------------------ */
const basePillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '16px',
    cursor: 'pointer',
    fontSize: '14px',
    whiteSpace: 'nowrap',
};

/* ------------------------------------------------------------------
   DraggablePill: used for multi-sort, wheels, bars, comparisons list.
   - 'item' is an object. We show item.label or item.field as text
   - onClick => open config
   - onRemove => delete
------------------------------------------------------------------ */
function DraggablePill({ item, dragId, index, onClick, onRemove, colorStyle }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: dragId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 9999 : 1,
        margin: 4,
        display: 'inline-block',
        position: 'relative',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div
                style={{
                    ...basePillStyle,
                    ...(colorStyle || {}),
                }}
            >
                {/* The drag handle */}
                <span {...listeners} style={{ cursor: 'grab', marginRight: 4 }}>
          ≡
        </span>

                {/* Clicking in the middle => open config */}
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        onClick(item, index);
                    }}
                >
          {item.label || item.field || 'Untitled'}
        </span>

                {/* The "x" remove button */}
                <span
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item, index);
                    }}
                >
          x
        </span>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------
   SingleSelectPill: let user pick exactly 1 item from 'options'
   If 'value' is empty, user sees a '+ Add' link; otherwise a green pill.
------------------------------------------------------------------ */
function SingleSelectPill({ value, options, label, onChange }) {
    const [isSelecting, setIsSelecting] = useState(false);

    const handleSelect = (selectedVal) => {
        onChange(selectedVal);
        setIsSelecting(false);
    };

    const selectedOpt = options.find((o) => o.value === value);

    return (
        <div style={{ marginBottom: 12 }}>
            <Text strong>{label}</Text>
            <div style={{ marginTop: 8 }}>
                {value ? (
                    <div style={basePillStyle} onClick={() => setIsSelecting(true)}>
                        {selectedOpt ? selectedOpt.label : value}
                    </div>
                ) : (
                    <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => setIsSelecting(true)}
                        style={{ paddingLeft: 0 }}
                    >
                        Add {label}
                    </Button>
                )}

                {isSelecting && (
                    <Select
                        showSearch
                        open
                        autoFocus
                        style={{ width: 220, marginLeft: 8 }}
                        value={value}
                        onBlur={() => setIsSelecting(false)}
                        onChange={handleSelect}
                        options={options}
                    />
                )}
            </div>
        </div>
    );
}

// Update MetricConfig function in ScorecardConfigModal.js
// Replace it with this enhanced version

function MetricConfig({ item, onUpdate, availableFields, type }) {
    if (!item) return null;

    // Calculate default values outside of conditional logic
    const calculatedMaxValue = item.calculation?.maxValue || 100;
    const shouldShowPercentage = item.calculation?.type === 'percentage' ||
        item.calculation?.type === 'average';

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Input
                addonBefore="Label"
                value={item.label || ''}
                onChange={(e) => onUpdate({ ...item, label: e.target.value })}
            />

            <Select
                style={{ width: '100%' }}
                value={item.calculation?.type}
                onChange={(val) =>
                    onUpdate({
                        ...item,
                        calculation: { ...item.calculation, type: val },
                    })
                }
            >
                <Select.Option value="average">Average</Select.Option>
                <Select.Option value="percentage">Percentage</Select.Option>
                <Select.Option value="count">Count</Select.Option>
                <Select.Option value="min">Minimum</Select.Option>
                <Select.Option value="max">Maximum</Select.Option>
                <Select.Option value="value">Raw Value</Select.Option>
            </Select>

            {item.calculation?.type === 'average' && (
                <Select
                    showSearch
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select fields"
                    value={item.calculation.fields || []}
                    onChange={(vals) =>
                        onUpdate({
                            ...item,
                            calculation: { ...item.calculation, fields: vals },
                        })
                    }
                >
                    {availableFields.map((f) => (
                        <Select.Option key={f.id} value={f.id}>
                            {f.title}
                        </Select.Option>
                    ))}
                </Select>
            )}

            {['value', 'min', 'max'].includes(item.calculation?.type) && (
                <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Select field"
                    value={item.calculation.field}
                    onChange={(val) =>
                        onUpdate({
                            ...item,
                            calculation: { ...item.calculation, field: val },
                        })
                    }
                >
                    {availableFields
                        .filter(f => f.type === 'number')
                        .map((f) => (
                            <Select.Option key={f.id} value={f.id}>
                                {f.title}
                            </Select.Option>
                        ))
                    }
                </Select>
            )}

            {(item.calculation?.type === 'percentage' || item.calculation?.type === 'count') && (
                <Input
                    addonBefore="Condition"
                    placeholder="e.g. record.callType === 'inbound'"
                    value={item.calculation.condition || ''}
                    onChange={(e) =>
                        onUpdate({
                            ...item,
                            calculation: {
                                ...item.calculation,
                                condition: e.target.value,
                            },
                        })
                    }
                />
            )}

            {/* Display options for Wheels and Bars */}
            {(type === 'wheels' || type === 'bars') && (
                <>
                    <div style={{ marginTop: 16, marginBottom: 8 }}>
                        <Text strong>Display Settings</Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                        <Switch
                            checked={item.displayAsPercentage === undefined ? shouldShowPercentage : item.displayAsPercentage}
                            onChange={(checked) =>
                                onUpdate({
                                    ...item,
                                    displayAsPercentage: checked,
                                })
                            }
                            size="small"
                        />
                        <span style={{ marginLeft: 8 }}>
                            Display as percentage
                        </span>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                        <Text>Max Value</Text>
                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <Radio.Group
                                value={item.useCustomMaxValue ? 'custom' : 'auto'}
                                onChange={(e) =>
                                    onUpdate({
                                        ...item,
                                        useCustomMaxValue: e.target.value === 'custom',
                                    })
                                }
                            >
                                <Radio value="auto">Auto</Radio>
                                <Radio value="custom">Custom</Radio>
                            </Radio.Group>

                            {item.useCustomMaxValue && (
                                <InputNumber
                                    min={1}
                                    value={item.maxValue || 100}
                                    onChange={(value) =>
                                        onUpdate({
                                            ...item,
                                            maxValue: value,
                                        })
                                    }
                                    style={{ width: 100 }}
                                />
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Tag style pickers */}
            {type === 'tags' && (
                <Space direction="vertical" style={{ marginTop: 12 }}>
                    <Text strong>Tag Style</Text>
                    <Space>
                        <span>Background:</span>
                        <Input
                            type="color"
                            value={item.style?.backgroundColor || '#f3f4f6'}
                            onChange={(e) =>
                                onUpdate({
                                    ...item,
                                    style: { ...item.style, backgroundColor: e.target.value },
                                })
                            }
                            style={{ width: 50, padding: 0 }}
                        />
                        <span>Text:</span>
                        <Input
                            type="color"
                            value={item.style?.color || '#6b7280'}
                            onChange={(e) =>
                                onUpdate({
                                    ...item,
                                    style: { ...item.style, color: e.target.value },
                                })
                            }
                            style={{ width: 50, padding: 0 }}
                        />
                    </Space>
                </Space>
            )}
        </Space>
    );
}

/* ------------------------------------------------------------------
   ComparisonConfig: form to edit a single comparison item
------------------------------------------------------------------ */
function ComparisonConfig({ item, onUpdate, availableMetrics }) {
    if (!item) return null;

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Input
                addonBefore="Label"
                value={item.label || ''}
                onChange={(e) => onUpdate({ ...item, label: e.target.value })}
            />

            <Select
                showSearch
                style={{ width: '100%' }}
                value={item.metric}
                onChange={(val) => onUpdate({ ...item, metric: val })}
                placeholder="Select metric"
            >
                {availableMetrics.map((m) => (
                    <Select.Option key={m.id} value={m.id}>
                        {m.label}
                    </Select.Option>
                ))}
            </Select>

            <Select
                style={{ width: '100%' }}
                value={item.baseline || 'average'}
                onChange={(val) => onUpdate({ ...item, baseline: val })}
            >
                <Select.Option value="average">Compare to average</Select.Option>
                <Select.Option value="min">Compare to min</Select.Option>
                <Select.Option value="max">Compare to max</Select.Option>
                <Select.Option value="count">Compare to count</Select.Option>
            </Select>

            <Select
                style={{ width: '100%' }}
                value={item.differenceType || 'percentage'}
                onChange={(val) => onUpdate({ ...item, differenceType: val })}
            >
                <Select.Option value="percentage">Percentage difference</Select.Option>
                <Select.Option value="absolute">Absolute difference</Select.Option>
            </Select>

            <Input
                addonBefore="Condition"
                placeholder="Optional condition, e.g. record.agentName === 'John'"
                value={item.condition || ''}
                onChange={(e) => onUpdate({ ...item, condition: e.target.value })}
            />

            <Space direction="vertical" style={{ marginTop: 12 }}>
                <Text strong>Positive/Negative Colors</Text>
                <Space>
                    <span>Positive:</span>
                    <Input
                        type="color"
                        value={item.style?.positiveColor || '#10b981'}
                        onChange={(e) =>
                            onUpdate({
                                ...item,
                                style: { ...item.style, positiveColor: e.target.value },
                            })
                        }
                        style={{ width: 50, padding: 0 }}
                    />
                    <span>Negative:</span>
                    <Input
                        type="color"
                        value={item.style?.negativeColor || '#dc2626'}
                        onChange={(e) =>
                            onUpdate({
                                ...item,
                                style: { ...item.style, negativeColor: e.target.value },
                            })
                        }
                        style={{ width: 50, padding: 0 }}
                    />
                </Space>
            </Space>
        </Space>
    );
}

/* ------------------------------------------------------------------
   MAIN: ScorecardConfigModal
------------------------------------------------------------------ */
export default function ScorecardConfigModal({
                                                 visible,
                                                 onClose,
                                                 config,
                                                 onConfigChange,
                                                 availableFields,
                                             }) {
    /**
     * We'll store your config in local state as:
     * {
     *   groupByField: string,
     *   showTrophies: boolean,
     *   sortCriteria: Array<{ id, field, direction }>,
     *   metrics: { wheels: [], bars: [], tags: [] },
     *   showComparisons: boolean,
     *   comparisons: Array<{
     *     id: string,
     *     label: string,
     *     metric: string,
     *     baseline: 'average'|'min'|'max'|'count',
     *     differenceType: 'percentage'|'absolute',
     *     condition?: string,
     *     style: { positiveColor: string, negativeColor: string }
     *   }>
     * }
     * Then on "Apply", we convert back to the old shape:
     * - config.sorting = { field, direction } from the first item in sortCriteria
     * - config.comparison = single {enabled, fields, labels, format, ...}
     * - config.showTrophies => also stored in config.general.showTrophies for older code
     */
    const [localConfig, setLocalConfig] = useState({
        groupByField: 'agentName',
        showTrophies: true,
        sortCriteria: [],
        metrics: { wheels: [], bars: [], tags: [] },
        showComparisons: true,
        comparisons: [],
    });

    // Tab + selection state
    const [activeTab, setActiveTab] = useState('general');
    const [selectedItem, setSelectedItem] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    // Merge existing config into local state when modal opens
    useEffect(() => {
        if (!visible) return;

        setActiveTab('general');
        setSelectedItem(null);
        setIsAdding(false);

        // Ensure we copy the sorting configuration correctly
        const initSorting = config.sorting ? { ...config.sorting } : {
            field: 'qualityScore',
            direction: 'desc'
        };

        // Possibly the user had an older config with .comparison.*
        // We'll convert that to localConfig.comparisons if .comparisons is empty:
        let fallbackComparisons = config.comparisons || [];
        if (
            (!fallbackComparisons || fallbackComparisons.length === 0) &&
            config.comparison?.enabled &&
            Array.isArray(config.comparison?.fields)
        ) {
            fallbackComparisons = config.comparison.fields.map((fId) => ({
                id: `cmp-${fId}`,
                label: config.comparison.labels?.[fId] || fId,
                metric: fId,
                baseline: 'average',
                differenceType: config.comparison.format || 'percentage',
                condition: '',
                style: {
                    positiveColor: config.comparison.positiveColor || '#10b981',
                    negativeColor: config.comparison.negativeColor || '#dc2626',
                },
            }));
        }

        // Set local config ensuring sorting is properly included
        setLocalConfig({
            groupByField: config.groupByField ?? 'agentName',
            showTrophies: config.showTrophies !== undefined ? config.showTrophies : true,
            sortCriteria: config.sortCriteria ?? [],
            metrics: config.metrics || { wheels: [], bars: [], tags: [] },
            showComparisons:
                config.showComparisons !== undefined
                    ? config.showComparisons
                    : !!config.comparison?.enabled,
            comparisons: fallbackComparisons,
            sorting: initSorting // Explicitly include sorting
        });
    }, [config, visible]);

    /* ------------------------------------------------
       GENERAL TAB
    --------------------------------------------------*/
    // Update this section in your ScorecardConfigModal.js file

// Inside the renderGeneralTab function, replace the sort criteria section
// with this improved version that only shows available aggregated metrics

    const renderGeneralTab = () => {
        // The "Group By Field" pill
        const groupOptions = availableFields.map((f) => ({
            label: f.title,
            value: f.id,
        }));

        // Get all available metrics for sorting (only those on scorecards)
        const availableSortingOptions = [
            // First option is always the grouping field itself
            {
                id: `${localConfig.groupByField || 'agentName'}`,
                label: `${groupOptions.find(opt => opt.value === (localConfig.groupByField || 'agentName'))?.label || 'Agent Name'} (alphabetical)`,
                field: localConfig.groupByField || 'agentName'
            },
            // Second option is Record Count
            {
                id: 'recordCount',
                label: 'Record Count',
                field: 'recordCount'
            },
            // Then add all wheel metrics
            ...(localConfig.metrics?.wheels || []).map(wheel => ({
                id: wheel.id,
                label: wheel.label || wheel.id,
                field: wheel.id
            })),
            // Then add all bar metrics
            ...(localConfig.metrics?.bars || []).map(bar => ({
                id: bar.id,
                label: bar.label || bar.id,
                field: bar.id
            })),
            // Then add all tag metrics
            ...(localConfig.metrics?.tags || []).map(tag => ({
                id: tag.id,
                label: tag.label || tag.id,
                field: tag.id
            }))
        ];

// Multi-sort logic with DnD - but limited to valid options
        const handleSortDragEnd = ({active, over}) => {
            if (!over) return;
            if (active.id !== over.id) {
                const oldIndex = localConfig.sortCriteria.findIndex(
                    (_, i) => `sort-${i}` === active.id
                );
                const newIndex = localConfig.sortCriteria.findIndex(
                    (_, i) => `sort-${i}` === over.id
                );
                if (oldIndex < 0 || newIndex < 0) return;
                const newArr = arrayMove(localConfig.sortCriteria, oldIndex, newIndex);
                setLocalConfig({...localConfig, sortCriteria: newArr});

                // Also update the main sorting configuration with the first item
                if (newArr.length > 0) {
                    setLocalConfig(prev => ({
                        ...prev,
                        sorting: {
                            field: newArr[0].field,
                            direction: newArr[0].direction
                        }
                    }));
                }
            }
        };

        return (
            <div style={{padding: 16}}>
                <SingleSelectPill
                    label="Group By Field"
                    value={localConfig.groupByField}
                    onChange={(val) =>
                        setLocalConfig((prev) => ({...prev, groupByField: val}))
                    }
                    options={groupOptions}
                />

                <div style={{marginTop: 16, marginBottom: 4}}>
                    <Text strong>Sort Criteria</Text>
                </div>
                <DndContext collisionDetection={closestCenter} onDragEnd={handleSortDragEnd}>
                    <SortableContext
                        items={localConfig.sortCriteria.map((_, i) => `sort-${i}`)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div style={{marginTop: 8, display: 'flex', flexWrap: 'wrap'}}>
                            {localConfig.sortCriteria.map((sc, i) => {
                                const arrow = sc.direction === 'asc' ? '↑' : '↓';
                                // Find proper label from available options
                                const option = availableSortingOptions.find(opt => opt.field === sc.field);
                                const label = option ? `${option.label} ${arrow}` : `${sc.field} ${arrow}`;
                                const dragId = `sort-${i}`;
                                return (
                                    <DraggablePill
                                        key={dragId}
                                        dragId={dragId}
                                        item={{...sc, label}}
                                        index={i}
                                        onClick={(itm, idx) => {
                                            // Toggle direction on click
                                            const newDir = itm.direction === 'asc' ? 'desc' : 'asc';
                                            const newList = [...localConfig.sortCriteria];
                                            newList[idx] = {...newList[idx], direction: newDir};
                                            setLocalConfig({...localConfig, sortCriteria: newList});

                                            // Update the main sorting configuration if this is the first item
                                            if (idx === 0) {
                                                setLocalConfig(prev => ({
                                                    ...prev,
                                                    sorting: {
                                                        field: newList[0].field,
                                                        direction: newList[0].direction
                                                    }
                                                }));
                                            }
                                        }}
                                        onRemove={(itm, idx) => {
                                            const newList = localConfig.sortCriteria.filter((_, j) => j !== idx);
                                            setLocalConfig({...localConfig, sortCriteria: newList});

                                            // Update the main sorting configuration with the new first item
                                            if (newList.length > 0) {
                                                setLocalConfig(prev => ({
                                                    ...prev,
                                                    sorting: {
                                                        field: newList[0].field,
                                                        direction: newList[0].direction
                                                    }
                                                }));
                                            }
                                        }}
                                    />
                                );
                            })}
                            {isAdding ? (
                                <Select
                                    autoFocus
                                    showSearch
                                    open
                                    style={{width: 220, marginLeft: 4}}
                                    placeholder="Select sort field"
                                    onBlur={() => setIsAdding(false)}
                                    onChange={(val) => {
                                        const option = availableSortingOptions.find(opt => opt.field === val);
                                        const newItem = {
                                            id: generateId(),
                                            field: val,
                                            direction: 'desc', // Default to descending
                                        };

                                        const newCriteria = [...localConfig.sortCriteria, newItem];
                                        setLocalConfig(prev => ({
                                            ...prev,
                                            sortCriteria: newCriteria
                                        }));

                                        // If this is the first sort criteria, also update the main sorting config
                                        if (localConfig.sortCriteria.length === 0) {
                                            setLocalConfig(prev => ({
                                                ...prev,
                                                sorting: {
                                                    field: val,
                                                    direction: 'desc'
                                                }
                                            }));
                                        }

                                        setIsAdding(false);
                                    }}
                                    // Only show options that aren't already used in sort criteria
                                    options={availableSortingOptions
                                        .filter(opt => !localConfig.sortCriteria.some(sc => sc.field === opt.field))
                                        .map(opt => ({
                                            label: opt.label,
                                            value: opt.field
                                        }))
                                    }
                                />
                            ) : (
                                <Button
                                    type="link"
                                    icon={<PlusOutlined/>}
                                    onClick={() => setIsAdding(true)}
                                >
                                    Add sort
                                </Button>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>

                <div style={{marginTop: 8, color: '#6b7280', fontSize: '14px'}}>
                    <em>Note: First sort criteria is used as the primary sort</em>
                </div>

                <div style={{marginTop: 24}}>
                    <Switch
                        checked={localConfig.showTrophies}
                        onChange={(checked) =>
                            setLocalConfig((prev) => ({...prev, showTrophies: checked}))
                        }
                    />
                    <span style={{marginLeft: 8}}>Show Trophies</span>
                </div>
            </div>
        );
    };

    /* ------------------------------------------------
       WHEELS / BARS / TAGS TABS
         - Draggable pills
         - Clicking => opens MetricConfig
    --------------------------------------------------*/
    const handleMetricDragEnd = (category, event) => {
        const { active, over } = event;
        if (!over) return;
        if (active.id === over.id) return;

        const items = localConfig.metrics[category] || [];
        const oldIndex = items.findIndex((itm) => `${category}-${itm.id}` === active.id);
        const newIndex = items.findIndex((itm) => `${category}-${itm.id}` === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const newItems = arrayMove(items, oldIndex, newIndex);
        setLocalConfig((prev) => ({
            ...prev,
            metrics: {
                ...prev.metrics,
                [category]: newItems,
            },
        }));
    };

    const renderMetricsTab = (category) => {
        const items = localConfig.metrics[category] || [];

        return (
            <DndContext
                collisionDetection={closestCenter}
                onDragEnd={(evt) => handleMetricDragEnd(category, evt)}
            >
                <SortableContext
                    items={items.map((itm) => `${category}-${itm.id}`)}
                    strategy={verticalListSortingStrategy}
                >
                    <div style={{ marginBottom: 16 }}>
                        {items.map((item, i) => {
                            const dragId = `${category}-${item.id}`;
                            // For Tags, we apply item.style to the pill; for Wheels/Bars, default green
                            let colorStyle = {};
                            if (category === 'tags') {
                                colorStyle = {
                                    backgroundColor: item.style?.backgroundColor || '#f3f4f6',
                                    color: item.style?.color || '#6b7280',
                                };
                            }
                            return (
                                <DraggablePill
                                    key={dragId}
                                    dragId={dragId}
                                    item={item}
                                    index={i}
                                    onClick={(itm) => setSelectedItem(itm)}
                                    onRemove={(itm) => {
                                        const newList = items.filter((x) => x.id !== itm.id);
                                        setLocalConfig((prev) => ({
                                            ...prev,
                                            metrics: {
                                                ...prev.metrics,
                                                [category]: newList,
                                            },
                                        }));
                                        if (selectedItem?.id === itm.id) setSelectedItem(null);
                                    }}
                                    colorStyle={colorStyle}
                                />
                            );
                        })}
                        {isAdding ? (
                            <Select
                                autoFocus
                                showSearch
                                open
                                style={{ width: 200, marginLeft: 4 }}
                                placeholder="Select field"
                                onBlur={() => setIsAdding(false)}
                                onChange={(val) => {
                                    const fieldObj = availableFields.find((f) => f.id === val);
                                    if (fieldObj) {
                                        const newMetric = {
                                            id: generateId(),
                                            label: fieldObj.title,
                                            field: fieldObj.id,
                                            calculation: {
                                                type: category === 'tags' ? 'count' : 'average',
                                                fields: category !== 'tags' ? [fieldObj.id] : undefined,
                                            },
                                            style:
                                                category === 'tags'
                                                    ? { backgroundColor: '#f3f4f6', color: '#6b7280' }
                                                    : undefined,
                                        };
                                        setLocalConfig((prev) => ({
                                            ...prev,
                                            metrics: {
                                                ...prev.metrics,
                                                [category]: [...prev.metrics[category], newMetric],
                                            },
                                        }));
                                        setSelectedItem(newMetric);
                                    }
                                    setIsAdding(false);
                                }}
                                options={availableFields.map((f) => ({
                                    label: f.title,
                                    value: f.id,
                                }))}
                            />
                        ) : (
                            <Button
                                type="link"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAdding(true)}
                            >
                                Add {category.slice(0, -1)}
                            </Button>
                        )}
                    </div>
                </SortableContext>
            </DndContext>
        );
    };

    const renderMetricConfigForm = () => {
        if (!selectedItem) return null;
        // figure out which category this item belongs to
        const cat = ['wheels', 'bars', 'tags'].find((c) =>
            localConfig.metrics[c].some((x) => x.id === selectedItem.id)
        );
        if (!cat) return null;

        return (
            <div
                style={{
                    marginTop: 16,
                    padding: 16,
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                }}
            >
                <MetricConfig
                    item={selectedItem}
                    type={cat}
                    availableFields={availableFields}
                    onUpdate={(upd) => {
                        setSelectedItem(upd);
                        setLocalConfig((prev) => {
                            const arr = prev.metrics[cat];
                            const newArr = arr.map((x) => (x.id === upd.id ? upd : x));
                            return {
                                ...prev,
                                metrics: {
                                    ...prev.metrics,
                                    [cat]: newArr,
                                },
                            };
                        });
                    }}
                />
            </div>
        );
    };

    /* ------------------------------------------------
       COMPARISON TAB
         - showComparisons (toggle)
         - localConfig.comparisons[] => pills
         - clicking => open ComparisonConfig
    --------------------------------------------------*/
    const handleComparisonDragEnd = ({ active, over }) => {
        if (!over) return;
        if (active.id !== over.id) {
            const oldIndex = localConfig.comparisons.findIndex((c) => `comp-${c.id}` === active.id);
            const newIndex = localConfig.comparisons.findIndex((c) => `comp-${c.id}` === over.id);
            if (oldIndex < 0 || newIndex < 0) return;

            const newArr = arrayMove(localConfig.comparisons, oldIndex, newIndex);
            setLocalConfig({ ...localConfig, comparisons: newArr });
        }
    };

    const renderComparisonsTab = () => {
        // We only let them pick from metrics that exist in wheels/bars so
        // AgentScorecard won't crash. If they pick a field that doesn't exist in
        // wheels/bars, calculateMetricValue hits undefined.
        const barsAndWheels = [
            ...(localConfig.metrics.wheels || []),
            ...(localConfig.metrics.bars || []),
        ];
        const compMetrics = barsAndWheels.map((m) => ({ id: m.id, label: m.label }));

        return (
            <div style={{ padding: 16 }}>
                <div style={{ marginBottom: 16 }}>
                    <Text strong>Enable Comparisons</Text>
                    <Switch
                        style={{ marginLeft: 8 }}
                        checked={localConfig.showComparisons}
                        onChange={(ch) => setLocalConfig({ ...localConfig, showComparisons: ch })}
                    />
                </div>
                <DndContext collisionDetection={closestCenter} onDragEnd={handleComparisonDragEnd}>
                    <SortableContext
                        items={localConfig.comparisons.map((cmp) => `comp-${cmp.id}`)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div style={{ marginBottom: 16 }}>
                            {localConfig.comparisons.map((cmp, i) => {
                                const dragId = `comp-${cmp.id}`;
                                return (
                                    <DraggablePill
                                        key={dragId}
                                        dragId={dragId}
                                        item={cmp}
                                        index={i}
                                        onClick={(it) => setSelectedItem(it)}
                                        onRemove={(it) => {
                                            const arr = localConfig.comparisons.filter((x) => x.id !== it.id);
                                            setLocalConfig({ ...localConfig, comparisons: arr });
                                            if (selectedItem?.id === it.id) setSelectedItem(null);
                                        }}
                                        // Standard green for comparison pills
                                        colorStyle={{
                                            backgroundColor: '#10b981',
                                            color: '#fff',
                                        }}
                                    />
                                );
                            })}
                            {isAdding ? (
                                <Select
                                    autoFocus
                                    showSearch
                                    open
                                    style={{ width: 220, marginLeft: 4 }}
                                    placeholder="Select metric"
                                    onBlur={() => setIsAdding(false)}
                                    onChange={(val) => {
                                        const newCmp = {
                                            id: generateId(),
                                            label: val,
                                            metric: val,
                                            baseline: 'average',
                                            differenceType: 'percentage',
                                            condition: '',
                                            style: {
                                                positiveColor: '#10b981',
                                                negativeColor: '#dc2626',
                                            },
                                        };
                                        setLocalConfig((prev) => ({
                                            ...prev,
                                            comparisons: [...prev.comparisons, newCmp],
                                        }));
                                        setSelectedItem(newCmp);
                                        setIsAdding(false);
                                    }}
                                    // Only show bars/wheels so we don't crash in AgentScorecard
                                    options={compMetrics.map((m) => ({
                                        label: m.label,
                                        value: m.id,
                                    }))}
                                />
                            ) : (
                                <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsAdding(true)}
                                >
                                    Add Comparison
                                </Button>
                            )}
                        </div>
                    </SortableContext>
                </DndContext>

                {selectedItem &&
                localConfig.comparisons.some((x) => x.id === selectedItem.id) && (
                    <div style={{ padding: 16, border: '1px solid #f0f0f0', borderRadius: 4 }}>
                        <ComparisonConfig
                            item={selectedItem}
                            availableMetrics={compMetrics}
                            onUpdate={(upd) => {
                                setSelectedItem(upd);
                                setLocalConfig((prev) => {
                                    const newArr = prev.comparisons.map((c) =>
                                        c.id === upd.id ? upd : c
                                    );
                                    return { ...prev, comparisons: newArr };
                                });
                            }}
                        />
                    </div>
                )}
            </div>
        );
    };

    /* ------------------------------------------------
       On Apply =>
         1) Enforce default sortCriteria if empty => [ { field:'qualityScore',direction:'desc' } ]
         2) Convert multi-sort => config.sorting w/ only the first item
         3) Convert comparisons => old shape config.comparison
         4) Write showTrophies => config.showTrophies and config.general.showTrophies
         5) Pass final object to onConfigChange
    --------------------------------------------------*/
    const handleApply = () => {
        // 1) If user never set any sort, default to 'qualityScore desc'
        if (!localConfig.sortCriteria || localConfig.sortCriteria.length === 0) {
            localConfig.sortCriteria = [
                {
                    id: generateId(),
                    field: 'qualityScore',
                    direction: 'desc',
                },
            ];
        }

        // 2) Take the first sort item => old config.sorting
        const primarySort = localConfig.sortCriteria[0];

        // 3) Rebuild old config.comparison from showComparisons + localConfig.comparisons
        //    We'll only store one differenceType + color from the first comparison item,
        //    because the old shape can't hold multiple sets of color/diffType.
        let oldComparison = { enabled: false, fields: [], labels: {}, format: 'percentage' };
        if (localConfig.showComparisons && localConfig.comparisons.length > 0) {
            const firstCmp = localConfig.comparisons[0];
            oldComparison = {
                enabled: true,
                fields: localConfig.comparisons.map((c) => c.metric),
                labels: localConfig.comparisons.reduce((acc, c) => {
                    acc[c.metric] = c.label;
                    return acc;
                }, {}),
                format: firstCmp.differenceType || 'percentage',
                positiveColor: firstCmp.style?.positiveColor || '#10b981',
                negativeColor: firstCmp.style?.negativeColor || '#dc2626',
            };
        }

        // 4) Build final config - ENSURE sorting is properly included
        const finalConfig = {
            ...localConfig,

            // Explicit inclusion of sorting config
            sorting: localConfig.sorting || {
                field: primarySort.field,
                direction: primarySort.direction,
            },

            // old single sorting object (for backward compatibility)
            /*sorting: {
                field: localConfig.sorting?.field || primarySort.field,
                direction: localConfig.sorting?.direction || primarySort.direction,
            },*/

            // old single comparison object
            comparison: oldComparison,

            // if your code expects config.general.showTrophies:
            general: {
                ...config.general, // keep any extra keys if needed
                showTrophies: localConfig.showTrophies,
            },

            // keep a top-level showTrophies too, in case your agent code reads that
            showTrophies: localConfig.showTrophies,
        };

        // Print final config to console for debugging
        if (process.env.NODE_ENV === 'development') {
            console.log('Applying scorecard configuration with sorting:',
                finalConfig.sorting);
        }

        onConfigChange(finalConfig);
        onClose();
    };

    return (
        <Modal
            title="Configure Scorecards"
            visible={visible}
            width={800}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button key="apply" type="primary" onClick={handleApply}>
                    Apply
                </Button>,
            ]}
        >
            <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                    setActiveTab(key);
                    setSelectedItem(null);
                    setIsAdding(false);
                }}
            >
                <TabPane tab="General" key="general">
                    {renderGeneralTab()}
                </TabPane>

                <TabPane tab="Wheels" key="wheels">
                    {renderMetricsTab('wheels')}
                    {renderMetricConfigForm()}
                </TabPane>

                <TabPane tab="Bars" key="bars">
                    {renderMetricsTab('bars')}
                    {renderMetricConfigForm()}
                </TabPane>

                <TabPane tab="Tags" key="tags">
                    {renderMetricsTab('tags')}
                    {renderMetricConfigForm()}
                </TabPane>

                <TabPane tab="Comparison" key="comparison">
                    {renderComparisonsTab()}
                </TabPane>
            </Tabs>
        </Modal>
    );
}
